import { PhotoStudioGenerationStepId } from '../generationSteps'
import { useCallback } from 'react'
import { logger } from 'utils/logging'
import { useFloorReplacementProcessor } from './useFloorReplacementProcessor.hook'

interface UseFloorReplacementFlowProps {
  assignmentId: string
  sourceVisualFilename: string
  getLatestPreviewFilename: () => string | null
  onProgressUpdate?: (step: string) => void
  onComplete?: (resultUrl: string) => void
  onError?: (error: any) => void
}

/**
 * Complete Floor Replacement flow hook
 * Handles processing and result retrieval
 * 
 * Usage:
 * 1. Call processFloorReplacement with template
 * 2. Hook handles processing, polling, and result retrieval
 * 3. Calls onComplete with result URL when finished
 */
export const useFloorReplacementFlow = ({
  assignmentId,
  sourceVisualFilename,
  getLatestPreviewFilename,
  onProgressUpdate,
  onComplete,
  onError
}: UseFloorReplacementFlowProps) => {
  // Floor Replacement processor
  const { processFloorReplacement, isProcessing, error } = useFloorReplacementProcessor({
    assignmentId,
    sourceVisualFilename,
    getLatestPreviewFilename,
    onProgressUpdate,
    onComplete,
    onError
  })

  const processFloorReplacementWithTemplate = useCallback(async (
    floorTemplate: string,
    previousJobId?: string
  ): Promise<string> => {
    try {
      onProgressUpdate?.(PhotoStudioGenerationStepId.START_PROCESSING)

      // Process Floor Replacement directly (no mask needed)
      const result = await processFloorReplacement(
        floorTemplate,
        previousJobId
      )

      if (result.success) {
        return result.jobId!
      } else {
        throw result.error
      }
    } catch (error) {
      logger.error('Floor Replacement: Flow failed', error, {
        context: {
          component: 'PhotoStudio',
          action: 'floorReplacementFlow',
          metadata: { assignmentId }
        }
      })
      onError?.(error)
      throw error
    }
  }, [processFloorReplacement, onProgressUpdate, onError, assignmentId])

  return {
    processFloorReplacementWithTemplate,
    isProcessing,
    error
  }
}
